import React, { useState, useMemo, useRef, useEffect } from "react";
import "./SiteDetailsAHJ.scss";
import CustomButton from "../components/common/CustomButton/CustomButton";
import CustomInspectionMRT from "./CustomInspectionMrt";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { useMediaQuery, Box } from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { useReactToPrint } from "react-to-print";
import InspectionReport from "./InspectionReport";
import { getAPIUrl } from "../components/common/helpers";
import axios from "axios";

const SiteDetailsAHJ = (props) => {
    const { siteBriefData } = props;
    const printDate = new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
    const [expandedPanels, setExpandedPanels] = useState([]);
    const [expandAll, setExpandAll] = useState(false);
    // Per-panel details are passed directly to the table when rendered
    const printRef = useRef();
    const [reportData, setReportData] = useState({});
    //const [isPrinting, setIsPrinting] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const isMobile = useMediaQuery("(max-width: 768px)" , "(min-width: 320px)");

const printResolveRef = useRef(null);
const [printDataReady, setPrintDataReady] = useState(false);

const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Inspection_${siteBriefData?.length > 0 ? siteBriefData[0].site_id : "Unknown"}`,
    onBeforePrint: async () => {
        const data = await loadAllPrintData();

        return new Promise((resolve) => {
            printResolveRef.current = resolve;
            setPrintDataReady(false);
            setReportData(data);
        });
    },
    onAfterPrint: () => {
        printResolveRef.current = null;
        setPrintDataReady(false);
        setIsGeneratingPdf(false);
    },
    onPrintError: (loc, error) => {
        console.error(`Print error at ${loc}`, error);
        printResolveRef.current = null;
        setIsGeneratingPdf(false);
    },
});

useEffect(() => {
    if (Object.keys(reportData).length > 0 && isGeneratingPdf) {
        setPrintDataReady(true);
    }
}, [reportData, isGeneratingPdf]);

useEffect(() => {
    if (!printDataReady || !printResolveRef.current) return;
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            const resolve = printResolveRef.current;
            printResolveRef.current = null;
            resolve?.();
        });
    });
}, [printDataReady]);

    // Define columns for the table
    const tableColumns = useMemo(
        () => [
            {
                accessorKey: "local_site_id",
                header: "Local Site ID",
                size: 100,
                grow: 1,
            },
            {
                accessorKey: "hardware_id",
                header: "Hardware ID",
                size: 140,
                grow: 2,
            },
            {
                accessorKey: "component_type",
                header: "Component Type",
                size: 250,
                grow: 4,
            },
            {
                accessorKey: "current_sw_version",
                header: "Current SW Version",
                size: 220,
                grow: 2,
            },
            {
                accessorKey: "most_recent_sw_version",
                header: "Most Recent SW Version",
                size: 170,
                grow: 2,
            },
            {
                accessorKey: "recent_release_date",
                header: "Most Recent Release Date",
                size: 180,
                grow: 2,
            },
            {
                accessorKey: "last_sw_update",
                header: "Last SW Update",
                size: 180,
                grow: 3,
            },
        ],
        []
    );


    // Data fetching function for printing
    // site_id is always 1; component_type_ids may be multiple
    const loadAllPrintData = async () => {
        const data = {};

        // Collect component type ids from the brief data
        const compIds = siteBriefData
            .map((c) => c.component_type_id ?? c.component_id ?? c.id)
            .filter(Boolean);
        if (compIds.length === 0) return data;

        const csv = compIds.join(",");
        const siteId = siteBriefData[0].site_id;
        try {
            const resp = await axios.get(getAPIUrl("generatePDFAHJ") + `?site_id=${encodeURIComponent(siteId)}&component_type_id=${encodeURIComponent(csv)}`);
            let rows = Array.isArray(resp.data.ajhData) ? resp.data.ajhData : [];
            // Map rows back to each component id
            siteBriefData.forEach((c) => {
                const compId = c.component_type;
                if (!compId) return;

                const matched = (rows || []).filter((r) =>
                    r.component_type === compId
                );

                data[compId] = matched;
            });
        } catch (err) {
    console.error("Error loading print data", err);
    siteBriefData.forEach((c) => {
        const compId = c.component_type;
        if (!compId) return;
        data[compId] = Array.isArray(reportData?.[compId]) ? reportData[compId] : [];
    });
}
        return data;
    };

    const handleSubmitPrint = () => {
    if (isGeneratingPdf || !siteBriefData?.length) return;
    setIsGeneratingPdf(true);
    reactToPrintFn();
};

    const togglePanel = (component_type_id, site_id) => {
        setExpandedPanels((prev) => {
            if (prev.includes(component_type_id)) {
                return prev.filter(id => id !== component_type_id);
            }
            return [...prev, component_type_id];
        });
    };

    const isPanelExpanded = (id) => expandAll || expandedPanels.includes(id);

    return (
        <div className="site-details">
            <div className="site-details-header">
                <div className="site-details-title" style={{ color: '#1f2937' }}>
                    <ApartmentIcon sx={{ "marginRight": "0.6rem" }} />
                    <span style={{ color: '#1f2937' }}>Grand Central Site ID </span>
                    {siteBriefData?.length > 0 && (
                        <span style={{ marginLeft: '0.5rem', color: '#1976d2', fontWeight: 600 }}>
                            {siteBriefData[0].site_id}
                        </span>
                    )}
                </div>
                {!isMobile && siteBriefData.length !== 0 && (

                    <CustomButton
                        handleClick={handleSubmitPrint}
                        label={isGeneratingPdf ? "Generating Print..." : "Print to PDF"}
                        buttonStyle="addColleagueFilled"
                    />

                )}
            </div>
            {isMobile ? (
                <div>
                    {
                        siteBriefData.map((component) => (
                            <div key={component.id} className="component-wrapper">
                                <div className="component-brief">
                                    <p>{component.component_type}</p>
                                    <p>Amount of Devices:<span>{component.amount_device}</span></p>
                                    <p>Amount of unique SW version: <span>{component.amount_unique_version}</span></p>
                                    <p>Last SW Update:<span>{component.software_update_date}</span></p>
                                    <div className="panel-footer">
                                        <button
                                            type="button"
                                            className="accordion-toggle-button"
                                            onClick={() => togglePanel(component.component_type_id, component.site_id)}
                                            aria-label={
                                                isPanelExpanded(component.component_type_id) ? "Collapse" : "Expand"
                                            }
                                        >
                                            {isPanelExpanded(component.component_type_id) ? (
                                                <KeyboardArrowUp />
                                            ) : (
                                                <KeyboardArrowDown />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                {isPanelExpanded(component.component_type_id) && (
                                    <>
                                        <div className="components-affected-banner">
                                            <Box style={{ display: "flex", alignItems: "center" }}>
                                                <span className="highLigterSpan"></span>
                                                <span className="highLightText">Components not on latest SW Release</span>
                                            </Box>
                                        </div>
                                        <div className="mrt-container">
                                            <CustomInspectionMRT
                                                columns={tableColumns}
                                                siteComponentDetails={{ component_id: component.component_type_id, site_id: component.site_id }}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        ))
                    }
                    {siteBriefData.length !== 0 && (
                        <div className="printButton">
                            <CustomButton
                                handleClick={handleSubmitPrint}
                                label={isGeneratingPdf ? "Generating Print..." : "Print to PDF"}
                                buttonStyle="addColleagueFilled"
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div>
                    {siteBriefData.map((component) => (
                        <div key={component.component_type_id} className="component-wrapper">
                            <div className="component-brief">
                                <div>{component.component_type}</div>
                                <div>Amount of Devices: <span>{component.amount_device}</span></div>
                                <div>Amount of unique SW version: <span>{component.amount_unique_version}</span></div>
                                <div>Last SW Update: <span>{component.software_update_date}</span></div>
                                <div>
                                    <button
                                        type="button"
                                        className="accordion-toggle-button"
                                        onClick={() => togglePanel(component.component_type_id, component.site_id)}
                                        aria-label={
                                            isPanelExpanded(component.component_type_id) ? "Collapse" : "Expand"
                                        }
                                    >
                                        {isPanelExpanded(component.component_type_id) ? (
                                            <KeyboardArrowUp sx={{ "width": "30rem" }} />
                                        ) : (
                                            <KeyboardArrowDown sx={{ "width": "30rem" }} />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {isPanelExpanded(component.component_type_id) && (
                                <>
                                    <div className="components-affected-banner">
                                        <Box style={{ display: "flex", alignItems: "center" }}>
                                            <span className="highLigterSpan"></span>
                                            <span className="highLightText">Components affected by new release</span>
                                        </Box>
                                    </div>
                                    <div className="mrt-container">
                                        <CustomInspectionMRT
                                            columns={tableColumns}
                                            siteComponentDetails={{ component_id: component.component_type_id, site_id: component.site_id }}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    ))
                    }
                </div>
            )}
            <div className="print-only">{
                isGeneratingPdf && (
                    <div className="pdf-loader">
                        Preparing PDF...
                    </div>
                )
            }
                <InspectionReport
                    ref={printRef}
                    siteId={siteBriefData?.[0]?.site_id ?? ""}
                    components={siteBriefData}
                    reportData={reportData}
                    printDate={printDate}
                />
            </div>
        </div>
    );
};

export default SiteDetailsAHJ;
